import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Table from '../../components/common/Table';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import Pagination from '../../components/common/Pagination';
import AdvancedSearch, { SearchField, SearchCondition, conditionsToQueryParams, queryParamsToConditions } from '../../components/common/AdvancedSearch';

interface User {
  id: string;
  email: string;
  name: string;
  nameKana: string;
  phone: string;
  postalCode: string;
  address: string;
  hireDate: string;
  status: string;
  roleId: string;
  departmentId: string;
  positionId: string;
  createdAt: string;
}

interface Role {
  id: string;
  name: string;
  code: string;
}

interface Department {
  id: string;
  name: string;
  code: string;
}

interface Position {
  id: string;
  name: string;
  code: string;
}

export default function UserList() {
  const { canCreate } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchConditions, setSearchConditions] = useState<SearchCondition[]>(() => {
    const saved = searchParams.get('conditions');
    return saved ? queryParamsToConditions(saved) : [];
  });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState('');
  const [inviting, setInviting] = useState(false);
  
  // ページネーション・ソート・表示件数（URL同期）
  const currentPage = parseInt(searchParams.get('page') || '1');
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = parseInt(searchParams.get('pageSize') || '20');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: searchParams.get('sortBy') || 'createdAt',
    direction: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc',
  });
  const setCurrentPage = (page: number | ((prev: number) => number)) => {
    const p = typeof page === 'function' ? page(currentPage) : page;
    setSearchParams(prev => { prev.set('page', String(p)); return prev; }, { replace: true });
  };
  const setPageSize = (size: number) => {
    setSearchParams(prev => { prev.set('pageSize', String(size)); prev.set('page', '1'); return prev; }, { replace: true });
  };
  const [createFormData, setCreateFormData] = useState({
    email: '',
    password: '',
    name: '',
    nameKana: '',
    phone: '',
    postalCode: '',
    address: '',
    hireDate: '',
    departmentId: '',
    positionId: '',
    roleId: '',
    status: 'active',
  });
  const [creating, setCreating] = useState(false);
  const [editFormData, setEditFormData] = useState({
    email: '',
    password: '',
    name: '',
    nameKana: '',
    phone: '',
    postalCode: '',
    address: '',
    hireDate: '',
    departmentId: '',
    positionId: '',
    roleId: '',
    status: '',
  });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);

  // 詳細検索フィールド定義
  const searchFields: SearchField[] = useMemo(() => [
    { key: 'name', label: '氏名', type: 'text', placeholder: '氏名で検索' },
    { key: 'nameKana', label: '氏名カナ', type: 'text', placeholder: 'カナで検索' },
    { key: 'email', label: 'メールアドレス', type: 'text', placeholder: 'メールで検索' },
    { key: 'phone', label: '電話番号', type: 'text', placeholder: '電話番号で検索' },
    { key: 'departmentId', label: '部署', type: 'select', options: departments.map(d => ({ value: d.id, label: d.name })) },
    { key: 'positionId', label: '役職', type: 'select', options: positions.map(p => ({ value: p.id, label: p.name })) },
    { key: 'roleId', label: '権限', type: 'select', options: roles.map(r => ({ value: r.id, label: r.name })) },
    { key: 'status', label: 'ステータス', type: 'select', options: [
      { value: 'active', label: '有効' },
      { value: 'pending', label: '招待中' },
      { value: 'inactive', label: '無効' },
    ]},
    { key: 'hireDate', label: '入社日', type: 'date' },
  ], [departments, positions, roles]);

  useEffect(() => {
    fetchRoles();
    fetchDepartments();
    fetchPositions();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [searchConditions, currentPage, pageSize, sortConfig]);

  // 検索条件をURLに保存（他パラメータを維持）
  useEffect(() => {
    setSearchParams(prev => {
      if (searchConditions.length > 0) {
        prev.set('conditions', conditionsToQueryParams(searchConditions));
      } else {
        prev.delete('conditions');
      }
      prev.set('page', '1');
      return prev;
    }, { replace: true });
  }, [searchConditions, setSearchParams]);

  const fetchRoles = async () => {
    try {
      const data = await api.get<{ items: Role[] }>('/api/roles');
      setRoles(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const data = await api.get<{ items: Department[] }>('/api/masters/departments');
      setDepartments(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const fetchPositions = async () => {
    try {
      const data = await api.get<{ items: Position[] }>('/api/masters/positions');
      setPositions(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error('Failed to fetch positions:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // 詳細検索条件を追加
      if (searchConditions.length > 0) {
        params.append('advancedSearch', JSON.stringify(searchConditions));
      }
      
      // ページネーション・ソートパラメータ
      params.append('page', currentPage.toString());
      params.append('pageSize', pageSize.toString());
      params.append('sortBy', sortConfig.key);
      params.append('sortOrder', sortConfig.direction);
      
      const data = await api.get<{ items: User[]; total: number; totalPages: number }>(`/api/users?${params}`);
      setUsers(Array.isArray(data?.items) ? data.items : []);
      setTotalPages(data?.totalPages ?? 1);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (conditions: SearchCondition[]) => {
    setSearchConditions(conditions);
    setCurrentPage(1);
  };

  const defaultSort = { key: 'createdAt', direction: 'desc' as const };
  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      let next: { key: string; direction: 'asc' | 'desc' };
      if (prev.key !== key) next = { key, direction: 'asc' };
      else if (prev.direction === 'asc') next = { key, direction: 'desc' };
      else next = defaultSort;
      setSearchParams(p => { p.set('sortBy', next.key); p.set('sortOrder', next.direction); p.set('page', '1'); return p; }, { replace: true });
      return next;
    });
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inviting) return;
    setInviting(true);
    try {
      await api.post('/api/users/invite', { email: inviteEmail, roleId: inviteRoleId });
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRoleId('');
      fetchUsers();
    } catch (error) {
      console.error('Failed to invite user:', error);
      toast.error('招待の送信に失敗しました');
    } finally {
      setInviting(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    try {
      // 空文字列をnullに変換
      const payload = {
        email: createFormData.email,
        password: createFormData.password,
        name: createFormData.name,
        nameKana: createFormData.nameKana || undefined,
        phone: createFormData.phone || undefined,
        postalCode: createFormData.postalCode || undefined,
        address: createFormData.address || undefined,
        hireDate: createFormData.hireDate || undefined,
        departmentId: createFormData.departmentId || undefined,
        positionId: createFormData.positionId || undefined,
        roleId: createFormData.roleId || undefined,
        status: createFormData.status,
      };
      await api.post('/api/users', payload);
      setShowCreateModal(false);
      setCreateFormData({
        email: '',
        password: '',
        name: '',
        nameKana: '',
        phone: '',
        postalCode: '',
        address: '',
        hireDate: '',
        departmentId: '',
        positionId: '',
        roleId: '',
        status: 'active',
      });
      fetchUsers();
      toast.success('ユーザーを登録しました');
    } catch (error: any) {
      console.error('Failed to create user:', error);
      toast.error(error?.message || 'ユーザーの登録に失敗しました');
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setEditFormData({
      email: user.email || '',
      password: '',
      name: user.name || '',
      nameKana: user.nameKana || '',
      phone: user.phone || '',
      postalCode: user.postalCode || '',
      address: user.address || '',
      hireDate: user.hireDate || '',
      departmentId: user.departmentId || '',
      positionId: user.positionId || '',
      roleId: user.roleId || '',
      status: user.status || 'active',
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser || saving) return;

    setSaving(true);
    try {
      // 空文字列をundefinedに変換
      const payload: Record<string, string | undefined> = {
        email: editFormData.email,
        name: editFormData.name,
        nameKana: editFormData.nameKana || undefined,
        phone: editFormData.phone || undefined,
        postalCode: editFormData.postalCode || undefined,
        address: editFormData.address || undefined,
        hireDate: editFormData.hireDate || undefined,
        departmentId: editFormData.departmentId || undefined,
        positionId: editFormData.positionId || undefined,
        roleId: editFormData.roleId || undefined,
        status: editFormData.status,
      };
      // パスワードが入力された場合のみ送信
      if (editFormData.password) {
        payload.password = editFormData.password;
      }
      await api.put(`/api/users/${selectedUser.id}`, payload);
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
      toast.success('ユーザーを更新しました');
    } catch (error: any) {
      console.error('Failed to update user:', error);
      toast.error(error?.message || 'ユーザーの更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (user: User) => {
    setDeleteTarget(user);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/users/${deleteTarget.id}`);
      fetchUsers();
      toast.success('ユーザーを削除しました');
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('ユーザーの削除に失敗しました');
    } finally {
      setDeleteTarget(null);
    }
  };

  const columns = [
    { key: 'name', header: '氏名', minWidth: '180px', sortable: true, mobileTitle: true, render: (user: User) => (
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
          {user.name?.charAt(0) || '?'}
        </div>
        <div className="min-w-0">
          <p className="font-medium">{user.name}</p>
          <p className="text-xs text-gray-500 truncate">{user.email}</p>
        </div>
      </div>
    )},
    { key: 'nameKana', header: '氏名（カナ）', minWidth: '120px', sortable: true, hideOnMobile: true },
    { key: 'status', header: 'ステータス', minWidth: '100px', sortable: true, render: (user: User) => (
      <Badge variant={user.status === 'active' ? 'success' : user.status === 'pending' ? 'warning' : 'danger'}>
        {user.status === 'active' ? '有効' : user.status === 'pending' ? '招待中' : '無効'}
      </Badge>
    )},
    { key: 'createdAt', header: '登録日', minWidth: '100px', sortable: true, hideOnMobile: true, render: (user: User) => new Date(user.createdAt).toLocaleDateString('ja-JP') },
    { key: 'actions', header: '', minWidth: '100px', render: (user: User) => (
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" onClick={() => handleEdit(user)} className="px-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          <span className="hidden lg:inline ml-1">編集</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => handleDelete(user)} className="px-2 text-red-500">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          <span className="hidden lg:inline ml-1">削除</span>
        </Button>
      </div>
    )},
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">ユーザー管理</h1>
        <div className="flex flex-col sm:flex-row gap-2">
          {canCreate('users') && (
            <Button onClick={() => setShowCreateModal(true)} className="w-full sm:w-auto">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              新規登録
            </Button>
          )}
          <Button onClick={() => setShowInviteModal(true)} variant="secondary" className="w-full sm:w-auto">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            招待で登録
          </Button>
        </div>
      </div>

      <Card>
        {/* 詳細検索 */}
        <div className="mb-4">
          <AdvancedSearch
            fields={searchFields}
            onSearch={handleSearch}
            quickSearchPlaceholder="名前またはメールで検索..."
            quickSearchField="name"
            initialConditions={searchConditions}
          />
        </div>
        <Table 
          columns={columns} 
          data={users} 
          loading={loading} 
          sortConfig={sortConfig}
          onSort={handleSort}
        />
        
        {/* ページネーション */}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 order-2 sm:order-1">
            <span className="hidden sm:inline">表示件数:</span>
            <Select
              value={pageSize.toString()}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="w-20 sm:w-24 text-sm"
            >
              <option value="10">10件</option>
              <option value="30">30件</option>
              <option value="50">50件</option>
              <option value="100">100件</option>
            </Select>
          </div>
          <div className="order-1 sm:order-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </Card>

      {/* 招待モーダル */}
      <Modal isOpen={showInviteModal} onClose={() => { setShowInviteModal(false); setInviteEmail(''); setInviteRoleId(''); }} title="ユーザーを招待">
        <form onSubmit={handleInvite} className="space-y-4">
          <Input
            label="メールアドレス"
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">権限</label>
            <Select
              value={inviteRoleId}
              onChange={(e) => setInviteRoleId(e.target.value)}
              required
            >
              <option value="">選択してください</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
              {roles.length === 0 && (
                <>
                  <option value="admin">管理者</option>
                  <option value="promoter">プロモーター</option>
                  <option value="project_manager">PM</option>
                  <option value="staff">担当</option>
                </>
              )}
            </Select>
          </div>
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={() => { setShowInviteModal(false); setInviteEmail(''); setInviteRoleId(''); }} className="w-full sm:w-auto">
              キャンセル
            </Button>
            <Button type="submit" disabled={inviting} className="w-full sm:w-auto">
              {inviting ? '送信中...' : '招待を送信'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* 新規登録モーダル */}
      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); setCreateFormData({ email: '', password: '', name: '', nameKana: '', phone: '', postalCode: '', address: '', hireDate: '', departmentId: '', positionId: '', roleId: '', status: 'active' }); }} title="新規ユーザー登録">
        <form onSubmit={handleCreate} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="メールアドレス"
              type="email"
              value={createFormData.email}
              onChange={(e) => setCreateFormData(prev => ({ ...prev, email: e.target.value }))}
              required
            />
            <Input
              label="パスワード"
              type="password"
              value={createFormData.password}
              onChange={(e) => setCreateFormData(prev => ({ ...prev, password: e.target.value }))}
              required
              placeholder="8文字以上、大文字・小文字・数字・記号を含む"
            />
            <Input
              label="氏名"
              value={createFormData.name}
              onChange={(e) => setCreateFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
            <Input
              label="氏名（カナ）"
              value={createFormData.nameKana}
              onChange={(e) => setCreateFormData(prev => ({ ...prev, nameKana: e.target.value }))}
            />
            <Input
              label="電話番号"
              type="tel"
              value={createFormData.phone}
              onChange={(e) => setCreateFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="090-1234-5678"
            />
            <Input
              label="入社日"
              type="date"
              value={createFormData.hireDate}
              onChange={(e) => setCreateFormData(prev => ({ ...prev, hireDate: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input
              label="郵便番号"
              value={createFormData.postalCode}
              onChange={(e) => setCreateFormData(prev => ({ ...prev, postalCode: e.target.value }))}
              placeholder="123-4567"
            />
            <div className="sm:col-span-2">
              <Input
                label="住所"
                value={createFormData.address}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="東京都渋谷区..."
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">部署</label>
              <Select
                value={createFormData.departmentId}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, departmentId: e.target.value }))}
              >
                <option value="">選択してください</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">役職</label>
              <Select
                value={createFormData.positionId}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, positionId: e.target.value }))}
              >
                <option value="">選択してください</option>
                {positions.map(pos => (
                  <option key={pos.id} value={pos.id}>{pos.name}</option>
                ))}
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">権限</label>
              <Select
                value={createFormData.roleId}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, roleId: e.target.value }))}
                required
              >
                <option value="">選択してください</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ステータス</label>
              <Select
                value={createFormData.status}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="active">有効</option>
                <option value="pending">招待中</option>
                <option value="inactive">無効</option>
              </Select>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={() => { setShowCreateModal(false); setCreateFormData({ email: '', password: '', name: '', nameKana: '', phone: '', postalCode: '', address: '', hireDate: '', departmentId: '', positionId: '', roleId: '', status: 'active' }); }} className="w-full sm:w-auto">
              キャンセル
            </Button>
            <Button type="submit" disabled={creating} className="w-full sm:w-auto">
              {creating ? '登録中...' : '登録'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* 編集モーダル */}
      <Modal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSelectedUser(null); }} title="ユーザーを編集">
        <form onSubmit={handleUpdate} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="メールアドレス"
              type="email"
              value={editFormData.email}
              onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
              required
            />
            <Input
              label="パスワード"
              type="password"
              value={editFormData.password}
              onChange={(e) => setEditFormData(prev => ({ ...prev, password: e.target.value }))}
              placeholder="変更する場合のみ入力"
            />
            <Input
              label="氏名"
              value={editFormData.name}
              onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
            <Input
              label="氏名（カナ）"
              value={editFormData.nameKana}
              onChange={(e) => setEditFormData(prev => ({ ...prev, nameKana: e.target.value }))}
            />
            <Input
              label="電話番号"
              type="tel"
              value={editFormData.phone}
              onChange={(e) => setEditFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="090-1234-5678"
            />
            <Input
              label="入社日"
              type="date"
              value={editFormData.hireDate}
              onChange={(e) => setEditFormData(prev => ({ ...prev, hireDate: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input
              label="郵便番号"
              value={editFormData.postalCode}
              onChange={(e) => setEditFormData(prev => ({ ...prev, postalCode: e.target.value }))}
              placeholder="123-4567"
            />
            <div className="sm:col-span-2">
              <Input
                label="住所"
                value={editFormData.address}
                onChange={(e) => setEditFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="東京都渋谷区..."
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">部署</label>
              <Select
                value={editFormData.departmentId}
                onChange={(e) => setEditFormData(prev => ({ ...prev, departmentId: e.target.value }))}
              >
                <option value="">選択してください</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">役職</label>
              <Select
                value={editFormData.positionId}
                onChange={(e) => setEditFormData(prev => ({ ...prev, positionId: e.target.value }))}
              >
                <option value="">選択してください</option>
                {positions.map(pos => (
                  <option key={pos.id} value={pos.id}>{pos.name}</option>
                ))}
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">権限</label>
              <Select
                value={editFormData.roleId}
                onChange={(e) => setEditFormData(prev => ({ ...prev, roleId: e.target.value }))}
              >
                <option value="">選択してください</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ステータス</label>
              <Select
                value={editFormData.status}
                onChange={(e) => setEditFormData(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="active">有効</option>
                <option value="inactive">無効</option>
              </Select>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={() => { setShowEditModal(false); setSelectedUser(null); }} className="w-full sm:w-auto">
              キャンセル
            </Button>
            <Button type="submit" disabled={saving} className="w-full sm:w-auto">
              {saving ? '更新中...' : '更新'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* 削除確認モーダル */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="削除確認">
        <p className="text-gray-700 dark:text-gray-300">「{deleteTarget?.name}」を削除しますか？</p>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>キャンセル</Button>
          <Button variant="danger" onClick={confirmDelete}>削除</Button>
        </div>
      </Modal>
    </div>
  );
}
